function cncEscapeHtml(value) {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

class ClickAndCollect {
  constructor(settings) {
    this.settings = settings || {};
    this.element = document.getElementById('cnc-container');
    this.resultsContainer = document.getElementById('cnc-results-container');
    this.mapContainer = document.getElementById('cnc-map-container');
    this.messages = {};
    this.mapFocus = null;
    this.gmap = null;
    this.search = null;

    if (!this.element || !this.resultsContainer) return;

    if (this.settings.enableMap && window.mapIsReady) {
      this.mapReady();
    }
  }

  addMessage(name, value, isError = false) {
    if (!this.resultsContainer) return;

    if (!this.messages[name]) {
      const message = new Message(name, value, isError);
      this.messages[name] = message;
      this.resultsContainer.appendChild(message.getElement());
    } else {
      this.messages[name].setValue(value);
      this.messages[name].setIsError(isError);
    }
  }

  removeMessage(name) {
    if (this.messages[name]) {
      const el = this.messages[name].getElement();
      if (el && el.parentNode) {
        el.parentNode.removeChild(el);
      }
      delete this.messages[name];
    }
  }

  clearErrors() {
    Object.entries(this.messages).forEach(([name, message]) => {
      if (message.isError) {
        this.removeMessage(name);
      }
    });
  }

  addAddressSearch() {
    this.search = new AddressSearch(this);
    if (this.mapContainer) {
      this.mapContainer.appendChild(this.search.getElement());
    }
  }

  addMap() {
    this.gmap = new GMap(this);
    if (this.mapContainer) {
      this.mapContainer.appendChild(this.gmap.getElement());
      this.gmap.initMap();
    }
  }

  mapReady() {
    if (!this.settings.enableMap || !this.mapContainer) return;
    if (this.gmap) return;

    this.addAddressSearch();
    this.addMap();
  }

  searchAddress(address) {
    if (!window.google || !google.maps || !address || !address.trim()) {
      this.addMessage('addressNotFound', 'Please enter a valid postcode or address.', true);
      return;
    }

    const geocoder = new google.maps.Geocoder();
    const parent = this;

    geocoder.geocode({ address: address }, function(results, status) {
      if (status === 'OK' && results && results[0]) {
        parent.removeMessage('addressNotFound');
        parent.mapFocus = results[0].geometry.location;

        if (parent.results) {
          parent.results.setLocation(parent.mapFocus.lat(), parent.mapFocus.lng());
          parent.results.update();
        }

        if (parent.gmap) {
          parent.gmap.show();
        }
      } else {
        if (parent.results) {
          parent.results.setLocation(null, null);
        }
        parent.addMessage('addressNotFound', `${address} not found.`, true);
      }
    });
  }

  updateMap() {
    if (!this.gmap || !this.results || !this.mapFocus) return;
    this.gmap.searchLocationsNear(this.mapFocus, this.results.getAllOutlets());
  }
}

class ClickAndCollectCart extends ClickAndCollect {
  constructor(settings) {
    super(settings);

    if (!this.element) return;

    this.mode = null;
    this.modeButtons = {};
    this.outletContactFields = {};

    this.addModeButtons(['home', 'store']);
    this.addOutletContactFields(['address1', 'address2', 'address3', 'suburb', 'state', 'postcode', 'phone', 'email']);
    this.addOutletNameField();
    this.addOutletNumberField();
    this.addResults();
    this.addCartUpdateListener();

    this.setMode('home');
    this.updateCollectionAbility();

    if (!this.settings.enableMap && this.results) {
      this.results.update();
    }
  }

  addModeButtons(values) {
    values.forEach((value) => {
      const button = new ModeButton(this, value);
      this.modeButtons[value] = button;
      this.element.appendChild(button.getElement());
      this.element.appendChild(button.getLabel());
    });

    const modeInput = document.createElement('input');
    modeInput.id = 'cnc-mode-input';
    modeInput.type = 'hidden';
    modeInput.name = 'attributes[cnc-fulfilment-method]';
    modeInput.value = 'home';
    this.element.appendChild(modeInput);
    this.modeInput = modeInput;
  }

  addOutletContactFields(fieldNames) {
    fieldNames.forEach((fieldName) => {
      const field = new OutletContactField(fieldName);
      this.outletContactFields[fieldName] = field;
      this.element.appendChild(field.getElement());
    });
  }

  addOutletNumberField() {
    const field = new OutletContactField('number', 'cnc-outlet', 'attributes[cnc-outlet]');
    this.outletNumberField = field;
    this.element.appendChild(field.getElement());
  }

  addOutletNameField() {
    const field = new OutletContactField('name', 'cnc-store-name', 'attributes[cnc-store-name]');
    this.outletNameField = field;
    this.element.appendChild(field.getElement());
  }

  addResults() {
    this.results = new CartResults(this, this.settings.apiPrefix, this.settings.subdomain);
    this.resultsContainer.appendChild(this.results.getElement());
  }

  getMode() {
    return this.mode;
  }

  setMode(mode) {
    if (mode === this.mode) return;

    this.clearErrors();
    this.mode = mode;

    if (this.modeInput) {
      this.modeInput.value = mode;
    }

    Object.values(this.modeButtons).forEach((modeButton) => {
      if (modeButton.getValue() === mode) {
        modeButton.select();
      }
      modeButton.updateDisplay();
    });

    if (this.mode === 'home') {
      if (this.mapContainer) {
        this.mapContainer.style.display = 'none';
      }

      this.results.hide();
      this.results.selectNone();

      Object.values(this.outletContactFields).forEach((contactField) => {
        contactField.clear();
      });

      this.outletNameField.clear();
      this.outletNumberField.clear();

      this.addMessage('home', 'You will be able to select a home delivery method in the checkout.', false);
    } else if (this.mode === 'store') {
      if (this.mapContainer) {
        this.mapContainer.style.display = '';
      }

      this.results.show();
      this.removeMessage('home');
      this.results.selectFirst();
      this.updateContactFields();
    }

    this.updateCheckoutButton();
  }

  updateCollectionAbility() {
    if ((this.results && this.results.doOutletsExist()) || this.settings.enableMap) {
      if (this.modeButtons.store) {
        this.modeButtons.store.show();
      }
      this.removeMessage('noLocations');
    } else {
      this.setMode('home');
      if (this.modeButtons.store) {
        this.modeButtons.store.hide();
      }
      this.addMessage('noLocations', 'No locations available for store pickup.', true);
    }
  }

  updateContactFields() {
    const selectedOutlet = this.results.getSelectedOutlet();

    if (selectedOutlet) {
      Object.entries(this.outletContactFields).forEach(([key, contactField]) => {
        contactField.setValue(selectedOutlet.getContact()[key] || '');
      });

      this.outletNameField.setValue(selectedOutlet.getName() || '');
      this.outletNumberField.setValue(selectedOutlet.getExternalId() || '');
    }
  }

  updateCheckoutButton() {
    const checkoutButton =
      document.querySelector('button[name="checkout"]') ||
      document.querySelector('input[name="checkout"]');

    if (!checkoutButton) return;

    this.checkoutButton = checkoutButton;

    if (this.mode === 'store' && !this.results.areOutletsAvailable()) {
      checkoutButton.disabled = true;

      if (this.results.isLoaded()) {
        if (this.settings.enableMap && this.results.count() < 1) {
          this.removeMessage('pleaseUpdateCart');
          this.addMessage(
            'pleaseSearchAgain',
            'Sorry, no pickup locations are available in this area. Please search again or choose Home Delivery.',
            true
          );
        } else {
          this.removeMessage('pleaseSearchAgain');
          this.addMessage(
            'pleaseUpdateCart',
            'Sorry, no pickup locations are available for all items in your cart. Please remove items from your cart or choose Home Delivery.',
            true
          );
        }
      }
    } else {
      checkoutButton.disabled = false;
      this.removeMessage('pleaseUpdateCart');
      this.removeMessage('pleaseSearchAgain');
    }
  }

  addCartUpdateListener() {
    const cartTable = document.getElementById('CartProducts');
    if (!cartTable) return;

    const observer = new MutationObserver(() => {
      if (this.results && this.results.isLoaded()) {
        this.results.update();
      }
    });

    observer.observe(cartTable, {
      childList: true,
      subtree: true
    });
  }
}

class ClickAndCollectProduct extends ClickAndCollect {
  constructor(settings, initialVariantId) {
    super(settings);

    if (!this.element) return;

    this.initialVariantId = initialVariantId;
    this.addCheckStockButton();
    this.addResults();
    this.addVariantEventListener();

    if (this.mapContainer) {
      this.mapContainer.style.display = 'none';
    }
  }

  addCheckStockButton() {
    const checkStockButton = new CheckStockButton(this);
    this.element.appendChild(checkStockButton.getElement());
  }

  addResults() {
    this.results = new ProductResults(this, this.settings.apiPrefix, this.settings.subdomain, this.initialVariantId);
    this.resultsContainer.appendChild(this.results.getElement());
  }

  addVariantEventListener() {
    const form = document.querySelector('form[action="/cart/add"]');
    if (!form) return;

    form.addEventListener('click', () => {
      if (this.results.isLoaded()) {
        this.results.unload();
        this.clearErrors();

        if (this.settings.enableMap && this.mapContainer && this.gmap) {
          this.mapContainer.style.display = 'none';
          this.gmap.hide();
        }
      }
    });
  }

  reveal() {
    if (this.settings.enableMap) {
      if (this.mapContainer) {
        this.mapContainer.style.display = '';
      }
    } else {
      this.results.fetch();
    }
  }

  resultsLoaded() {
    if (this.settings.enableMap && this.results.count() < 1) {
      this.addMessage('pleaseSearchAgain', 'Sorry, no pickup locations are available in this area.', true);
    } else {
      this.removeMessage('pleaseSearchAgain');
    }
  }
}

class Renderable {
  getElement() {
    return this.element;
  }

  show() {
    if (this.element) this.element.style.display = '';
    if (this.label) this.label.style.display = '';
  }

  hide() {
    if (this.element) this.element.style.display = 'none';
    if (this.label) this.label.style.display = 'none';
  }
}

class AddressSearch extends Renderable {
  constructor(cnc) {
    super();
    this.cnc = cnc;
    this.createElement();
  }

  createElement() {
    this.element = document.createElement('div');
    this.element.classList.add('cnc-address-search');
    this.element.id = 'cnc-address-search';

    this.label = document.createElement('p');
    this.label.textContent = 'Search for a zip code of the location to pick up your order:';
    this.element.appendChild(this.label);

    this.input = document.createElement('input');
    this.input.type = 'text';
    this.input.classList.add('cnc-address-search-input');
    this.input.placeholder = 'Enter your postcode...';
    this.element.appendChild(this.input);

    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.classList.add('cnc-address-search-button');
    this.button.innerHTML = '<i class="fas fa-search"></i><span class="cnc-address-search-label">Search</span>';
    this.element.appendChild(this.button);

    this.button.addEventListener('click', () => {
      this.cnc.searchAddress(this.input.value);
    });

    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.button.click();
      }
    });
  }
}

class GMap extends Renderable {
  constructor(cnc) {
    super();
    this.cnc = cnc;
    this.gmap = null;
    this.markers = [];
    this.infoWindow = null;
    this.mapInitialised = false;
    this.createElement();
  }

  createElement() {
    this.element = document.createElement('div');
    this.element.id = 'cnc-map';
    this.hide();
  }

  initMap() {
    if (!window.google || !google.maps) return;

    const sydney = { lat: -33.863276, lng: 151.107977 };

    this.gmap = new google.maps.Map(this.element, {
      center: sydney,
      zoom: 11,
      zoomControl: true,
      mapTypeId: 'roadmap'
    });

    this.infoWindow = new google.maps.InfoWindow();
    this.mapInitialised = true;
  }

  clearLocations() {
    if (this.infoWindow) {
      this.infoWindow.close();
    }

    this.markers.forEach((marker) => marker.setMap(null));
    this.markers = [];
  }

  searchLocationsNear(center, outlets) {
    if (!this.gmap) return;

    this.clearLocations();

    const bounds = new google.maps.LatLngBounds();
    let markerCount = 0;

    Object.entries(outlets).forEach(([name, outlet]) => {
      const contact = outlet.getContact();
      const lat = parseFloat(contact.latitude);
      const lng = parseFloat(contact.longitude);

      if (Number.isNaN(lat) || Number.isNaN(lng)) return;

      const latlng = new google.maps.LatLng(lat, lng);

      this.createMarker(
        latlng,
        name,
        outlet.getAddressHtml(),
        outlet.data.distance
      );

      bounds.extend(latlng);
      markerCount += 1;
    });

    if (markerCount >= 1) {
      this.gmap.fitBounds(bounds);
      const zoom = this.gmap.getZoom();
      this.gmap.setZoom(zoom > 10 ? 10 : zoom);
    } else if (center) {
      this.gmap.setCenter(center);
    }
  }

  createMarker(latlng, name, address, distance) {
    let html = `<b>${cncEscapeHtml(name)}</b><br>`;

    if (distance) {
      html += `${cncEscapeHtml(distance)}km<br>`;
    }

    html += address;

    const marker = new google.maps.Marker({
      map: this.gmap,
      position: latlng
    });

    google.maps.event.addListener(marker, 'click', () => {
      this.infoWindow.setContent(html);
      this.infoWindow.open(this.gmap, marker);
    });

    this.markers.push(marker);
  }
}

class ModeButton extends Renderable {
  constructor(cnc, value) {
    super();
    this.cnc = cnc;
    this.value = value;
    this.createElement();
    this.createLabel();

    this.element.addEventListener('change', () => {
      this.cnc.setMode(this.element.value);
    });

    this.updateDisplay();
  }

  select() {
    if (!this.element.checked) {
      this.element.checked = true;
    }
  }

  updateDisplay() {
    if (this.element.checked) {
      this.label.classList.add('btn--secondary');
    } else {
      this.label.classList.remove('btn--secondary');
    }
  }

  createElement() {
    this.element = document.createElement('input');
    this.element.id = `cnc-${this.value}-radio`;
    this.element.type = 'radio';
    this.element.value = this.value;
    this.element.name = 'cnc-mode-radio';
  }

  createLabel() {
    this.label = document.createElement('label');
    this.label.setAttribute('for', `cnc-${this.value}-radio`);
    this.label.classList.add('btn');

    if (this.value === 'home') {
      this.label.innerHTML = '<i class="fas fa-globe-americas"></i><br>Home Delivery';
    } else if (this.value === 'store') {
      this.label.style.display = 'none';
      this.label.innerHTML = '<i class="fas fa-store"></i><br>Store Pickup';
    }
  }

  getLabel() {
    return this.label;
  }

  getValue() {
    return this.element.value;
  }
}

class CheckStockButton extends Renderable {
  constructor(cncProduct) {
    super();
    this.cncProduct = cncProduct;
    this.createElement();

    this.element.addEventListener('click', () => {
      const addToCartButton = document.querySelector('form[action="/cart/add"] button[type="submit"]');

      if (addToCartButton && addToCartButton.disabled) {
        this.cncProduct.addMessage('productUnavailable', 'Product variant is unavailable.', true);
      } else {
        this.cncProduct.removeMessage('productUnavailable');
        this.cncProduct.reveal();
      }
    });
  }

  createElement() {
    this.element = document.createElement('button');
    this.element.type = 'button';
    this.element.innerHTML = 'Check stock in store';
    this.element.classList.add('btn');
  }
}

class Outlet extends Renderable {
  constructor(cnc, data, cartItems) {
    super();
    this.cnc = cnc;
    this.data = data;
    this.createAvailabilityTable(cartItems);
    this.createElement();
    this.createLabel();

    this.element.addEventListener('change', () => {
      cnc.updateContactFields();
    });
  }

  getAvailability() {
    return this.availabilityTable && this.availabilityTable.isOutletAvailable();
  }

  getAvailabilityTable() {
    return this.availabilityTable;
  }

  createAvailabilityTable(cartItems) {
    this.availabilityTable = new AvailabilityTable(
      cartItems,
      this.data.stock || {},
      true,
      this.cnc.settings.showQuantity
    );
  }

  isSelected() {
    return this.element.checked;
  }

  select() {
    this.element.checked = true;
  }

  deselect() {
    this.element.checked = false;
  }

  getContact() {
    return this.data.contact || {};
  }

  getName() {
    return this.data.name || '';
  }

  getExternalId() {
    return this.data.external_id || '';
  }

  createElement() {
    this.element = document.createElement('input');
    this.element.id = `cnc-outlet-${this.data.external_id}`;
    this.element.type = 'radio';
    this.element.name = 'cnc-outlet-element';
    this.element.value = this.data.external_id;
    this.element.disabled = !this.getAvailability();
    this.element.classList.add('cnc-outlet');
  }

  createLabel() {
    this.label = document.createElement('label');
    this.label.setAttribute('for', `cnc-outlet-${this.data.external_id}`);
    this.label.classList.add('cnc-outlet-label');

    const storeDetails = document.createElement('div');
    storeDetails.classList.add('cnc-store-details');

    storeDetails.insertAdjacentHTML('beforeend', `<strong>${cncEscapeHtml(this.data.name)}</strong><br>`);

    if (this.data.distance) {
      storeDetails.insertAdjacentHTML('beforeend', `${cncEscapeHtml(this.data.distance)}km<br>`);
    }

    storeDetails.insertAdjacentHTML('beforeend', this.getAddressHtml());

    this.label.appendChild(storeDetails);
    this.label.appendChild(this.getAvailabilityTable().getElement());
  }

  getAddressHtml() {
    const contact = this.data.contact || {};
    let innerHtml = '';

    if (contact.address1) innerHtml += `${cncEscapeHtml(contact.address1)}<br>`;
    if (contact.address2) innerHtml += `${cncEscapeHtml(contact.address2)}<br>`;
    if (contact.address3) innerHtml += `${cncEscapeHtml(contact.address3)}<br>`;
    if (contact.suburb) innerHtml += `${cncEscapeHtml(contact.suburb)} `;
    if (contact.state) innerHtml += `${cncEscapeHtml(contact.state)} `;
    if (contact.postcode) innerHtml += `${cncEscapeHtml(contact.postcode)}`;
    innerHtml += '<br>';

    if (contact.phone) {
      const safePhone = cncEscapeHtml(contact.phone);
      innerHtml += `Phone: <a href="tel:${safePhone}">${safePhone}</a><br>`;
    }

    if (contact.email) {
      const safeEmail = cncEscapeHtml(contact.email);
      innerHtml += `Email: <a href="mailto:${safeEmail}">${safeEmail}</a>`;
    }

    return innerHtml;
  }

  getLabel() {
    return this.label;
  }
}

class ProductOutlet extends Outlet {
  constructor(cnc, data, cartItems) {
    super(cnc, data, cartItems);
  }

  createAvailabilityTable(cartItems) {
    this.availabilityTable = new AvailabilityTable(
      cartItems,
      this.data.stock || {},
      false,
      this.cnc.settings.showQuantity
    );
  }

  createElement() {
    this.element = document.createElement('div');
    this.element.classList.add('cnc-outlet');
  }

  createLabel() {
    this.label = document.createElement('div');
    this.label.classList.add('cnc-outlet-label', 'cnc-product-outlet-label');

    const storeDetails = document.createElement('div');
    storeDetails.classList.add('cnc-store-details');

    storeDetails.insertAdjacentHTML('beforeend', `<strong>${cncEscapeHtml(this.data.name)}</strong><br>`);
    storeDetails.insertAdjacentHTML('beforeend', this.getAddressHtml());

    this.label.appendChild(storeDetails);
    this.label.appendChild(this.getAvailabilityTable().getElement());
  }
}

class AvailabilityTable extends Renderable {
  constructor(cartItems, stockLevels, showTable, showQuantity) {
    super();
    this.products = [];
    this.showTable = showTable;
    this.showQuantity = showQuantity;
    this.updateProducts(cartItems, stockLevels || {});
    this.createElement();
  }

  updateProducts(cartItems, stockLevels) {
    if (cartItems && cartItems.length) {
      cartItems.forEach((cartItem) => {
        const product = Object.assign({}, cartItem);
        product.outletStock = stockLevels[cartItem.variant_id] || 0;
        this.products.push(product);
      });
    } else {
      Object.entries(stockLevels).forEach(([variantId, stockLevel]) => {
        this.products.push({
          variant_id: variantId,
          outletStock: stockLevel || 0,
          quantity: 1,
          title: 'Selected product'
        });
      });
    }
  }

  isOutletAvailable() {
    return this.products.every((product) => this.isProductAvailable(product));
  }

  isProductAvailable(product) {
    return Number(product.outletStock) >= Number(product.quantity);
  }

  createElement() {
    if (this.showTable) {
      this.createTableElement();
    } else {
      this.createTextElement();
    }
  }

  createTableElement() {
    this.element = document.createElement('table');
    this.element.classList.add('cnc-availability-table');

    const headingHtml = this.isOutletAvailable()
      ? '<tr><th colspan="3" class="cnc-heading-available">All items available</th></tr>'
      : '<tr><th colspan="3" class="cnc-heading-unavailable">Some items are not available</th></tr>';

    const head = document.createElement('thead');
    head.insertAdjacentHTML('beforeend', headingHtml);
    head.insertAdjacentHTML('beforeend', '<tr><th>Product</th><th>Qty</th><th>Available</th></tr>');
    this.element.appendChild(head);

    const body = document.createElement('tbody');

    this.products.forEach((product) => {
      let productTitle = cncEscapeHtml(product.title || 'Product');

      if (!this.isProductAvailable(product)) {
        if (product.outletStock > 0) {
          productTitle = `<span class="strike">${productTitle}</span> <span class="cnc-negative-text">(Insufficient stock)</span>`;
        } else {
          productTitle = `<span class="strike">${productTitle}</span> <span class="cnc-negative-text">(Out of stock)</span>`;
        }
      }

      const productAvailable = this.showQuantity
        ? this.isProductAvailable(product)
          ? `<span class="cnc-positive-text"><strong>${cncEscapeHtml(product.outletStock)}</strong></span>`
          : `<span class="cnc-negative-text"><strong>${cncEscapeHtml(product.outletStock)}</strong></span>`
        : this.isProductAvailable(product)
          ? '<i class="fas fa-check-circle cnc-positive-text"></i>'
          : '<i class="fas fa-times-circle cnc-negative-text"></i>';

      body.insertAdjacentHTML(
        'beforeend',
        `<tr><td>${productTitle}</td><td>${cncEscapeHtml(product.quantity)}</td><td>${productAvailable}</td></tr>`
      );
    });

    this.element.appendChild(body);
  }

  createTextElement() {
    this.element = document.createElement('p');
    this.element.classList.add('cnc-heading-availability');

    if (this.isOutletAvailable()) {
      if (this.showQuantity && this.products.length === 1) {
        this.element.textContent = `${this.products[0].outletStock} available`;
      } else {
        this.element.textContent = 'In stock';
      }
      this.element.classList.add('cnc-heading-available');
    } else {
      this.element.textContent = 'Out of stock';
      this.element.classList.add('cnc-heading-unavailable');
    }
  }
}

class OutletContactField extends Renderable {
  constructor(name, id, elementName) {
    super();
    this.name = name;
    this.createElement(id, elementName);
  }

  createElement(id, elementName) {
    this.element = document.createElement('input');
    this.element.type = 'hidden';
    this.element.classList.add('cnc-contact');
    this.element.id = id || `cnc-contact-${this.name}`;
    this.element.name = elementName || `attributes[cnc-store-${this.name}]`;
    this.element.value = '';
  }

  clear() {
    this.element.value = '';
  }

  setValue(value) {
    this.element.value = value || '';
  }
}

class Message extends Renderable {
  constructor(name, value, isError) {
    super();
    this.name = name;
    this.createElement();
    this.setValue(value);
    this.setIsError(isError);
  }

  createElement() {
    this.element = document.createElement('div');
    this.element.id = `cnc-message-${this.name}`;
    this.paragraph = document.createElement('p');
    this.element.appendChild(this.paragraph);
  }

  getValue() {
    return this.value;
  }

  setValue(value) {
    this.value = value;
    this.paragraph.textContent = value;
  }

  clear() {
    this.setValue('');
  }

  setIsError(bool) {
    this.isError = !!bool;
    if (this.isError) {
      this.element.classList.add('cnc-negative-text');
    } else {
      this.element.classList.remove('cnc-negative-text');
    }
  }
}

class Results extends Renderable {
  constructor(cnc, apiPrefix, subdomain) {
    super();
    this.cnc = cnc;
    this.apiPrefix = apiPrefix;
    this.subdomain = subdomain;
    this.outlets = {};
    this.loaded = false;
    this.lastUpdate = 0;
  }

  update() {}
  fetch() {}

  createElement() {
    this.element = document.createElement('div');
    this.element.id = 'cnc-results';
    this.element.style.display = 'none';
  }

  createList() {
    this.list = document.createElement('ul');
    this.list.id = 'cnc-outlets';
    this.list.classList.add('cart-attribute__field');
    this.element.appendChild(this.list);
  }

  setLocation(latitude, longitude) {
    this.latitude = latitude;
    this.longitude = longitude;
  }

  getAllOutlets() {
    return this.outlets;
  }

  count() {
    return Object.keys(this.outlets).length;
  }

  unload() {
    this.outlets = {};
    this.loaded = false;

    if (!this.list) return;

    while (this.list.firstChild) {
      this.list.removeChild(this.list.firstChild);
    }
  }

  renderOutlet(outlet) {
    const listItem = document.createElement('li');
    listItem.appendChild(outlet.getElement());
    listItem.appendChild(outlet.getLabel());
    this.list.appendChild(listItem);
  }

  isLoaded() {
    return this.loaded;
  }

  doOutletsExist() {
    return Object.keys(this.outlets).length > 0;
  }

  areOutletsAvailable() {
    return Object.values(this.outlets).some((outlet) => outlet.getAvailability());
  }
}

class CartResults extends Results {
  constructor(cnc, apiPrefix, subdomain) {
    super(cnc, apiPrefix, subdomain);
    this.createElement();
    this.createInfo();
    this.createList();
    this.variantIds = [];
    this.cartItems = [];
  }

  createInfo() {
    this.info = document.createElement('p');
    this.info.id = 'cnc-info';
    this.element.appendChild(this.info);
  }

  setInfo(value) {
    this.info.textContent = value || '';
  }

  selectNone() {
    Object.values(this.outlets).forEach((outlet) => {
      outlet.deselect();
      this.cnc.updateContactFields();
    });
  }

  selectFirst() {
    for (const outlet of Object.values(this.outlets)) {
      if (outlet.getAvailability()) {
        outlet.select();
        this.cnc.updateContactFields();
        return;
      }
    }
  }

  getSelectedOutlet() {
    for (const outlet of Object.values(this.outlets)) {
      if (outlet.isSelected()) {
        return outlet;
      }
    }
    return null;
  }

  update() {
    if (Date.now() - this.lastUpdate < 1000) return;
    this.lastUpdate = Date.now();

    const parent = this;
    Shopify.getCart(function(cart) {
      parent.cartItems = cart.items || [];
      parent.variantIds = parent.cartItems.map((item) => item.variant_id);
      parent.fetch();
    });
  }

  fetch() {
    this.unload();

    if (!this.variantIds.length) {
      this.setInfo('');
      this.cnc.updateCollectionAbility();
      this.cnc.updateCheckoutButton();
      return;
    }

    let url = `https://${this.apiPrefix}/api/shopify_stores/${this.subdomain}/outlets?variant_ids=${this.variantIds.join()}`;

    this.cartItems.forEach((cartItem) => {
      url += `&qty-${cartItem.variant_id}=${cartItem.quantity}`;
    });

    if (this.latitude && this.longitude) {
      url += `&latitude=${this.latitude}&longitude=${this.longitude}`;
    }

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to fetch pickup locations.');
        }
        return response.json();
      })
      .then((json) => {
        (json.outlets || []).forEach((outletJson) => {
          const outlet = new Outlet(this.cnc, outletJson, this.cartItems);
          this.outlets[outlet.getName()] = outlet;
          this.renderOutlet(outlet);
        });

        this.setInfo((json.outlets || []).length ? 'Please choose a pickup location:' : '');
        this.loaded = true;

        this.cnc.updateCollectionAbility();
        this.cnc.updateCheckoutButton();

        if (this.cnc.settings.enableMap) {
          this.cnc.updateMap();
        }

        if (this.cnc.getMode() === 'store') {
          this.selectFirst();
        }
      })
      .catch(() => {
        this.loaded = true;
        this.setInfo('');
        this.cnc.addMessage('locationsFetchError', 'Unable to load pickup locations right now. Please try again later.', true);
        this.cnc.updateCheckoutButton();
      });
  }
}

class ProductResults extends Results {
  constructor(cnc, apiPrefix, subdomain, initialVariantId) {
    super(cnc, apiPrefix, subdomain);
    this.createElement();
    this.createList();
    this.initialVariantId = initialVariantId;
  }

  createElement() {
    this.element = document.createElement('div');
    this.element.id = 'cnc-results';
  }

  update() {
    this.fetch();
  }

  fetch() {
    this.unload();

    let variantId = this.findGetParameter('variant');
    if (!variantId) {
      variantId = this.initialVariantId || this.getSelectedVariantIdFromHtml();
    }

    if (!variantId) {
      this.cnc.addMessage('variantMissing', 'No product variant selected.', true);
      return;
    }

    let url = `https://${this.apiPrefix}/api/shopify_stores/${this.subdomain}/outlets?variant_ids=${variantId}&qty-${variantId}=1`;

    if (this.latitude && this.longitude) {
      url += `&latitude=${this.latitude}&longitude=${this.longitude}`;
    }

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to fetch pickup locations.');
        }
        return response.json();
      })
      .then((json) => {
        (json.outlets || []).forEach((outletJson) => {
          const outlet = new ProductOutlet(this.cnc, outletJson);
          this.outlets[outlet.getName()] = outlet;
          this.renderOutlet(outlet);
        });

        this.loaded = true;

        if (this.cnc.settings.enableMap) {
          this.cnc.updateMap();
        }

        this.cnc.resultsLoaded();
      })
      .catch(() => {
        this.loaded = true;
        this.cnc.addMessage('locationsFetchError', 'Unable to load pickup locations right now. Please try again later.', true);
      });
  }

  getSelectedVariantIdFromHtml() {
    const select = document.getElementById('ProductSelect-product-template');
    if (select) {
      return select.options[select.selectedIndex].value;
    }
    return null;
  }

  findGetParameter(parameterName) {
    const params = new URLSearchParams(window.location.search);
    return params.get(parameterName);
  }
}